import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import DashboardLayout from '../../../components/layout/DashboardLayout';
import StatCard from './components/StatCard';
import EcfBadge, { AMBIENTE_BADGE } from './components/EcfBadge';
import { DOC_TYPE_LABEL } from './catalogos';
import { ecfService, type EcfSummary } from '../../../services/ecf.service';

/**
 * Panel e-CF: el estado de la facturación electrónica de un vistazo.
 *
 * Existe porque un comprobante que quedó sin enviar (se cayó el internet al cerrar la venta) no
 * se reintenta solo, y sin esta pantalla nadie se entera hasta la declaración mensual. El bloque
 * "Requiere atención" es el que responde a la pregunta real: ¿estoy al día?
 *
 * Todo sale de `GET /api/ecf/summary`, que cuenta en la base. No se traen las listas completas
 * para contarlas en el navegador: un año de facturación no cabe.
 */

type AlertTone = 'bad' | 'warn';

interface Alerta {
  tone: AlertTone;
  text: string;
  action?: { label: string; to: string };
}

export default function EcfPanelPage() {
  const navigate = useNavigate();
  const [summary, setSummary] = useState<EcfSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [syncing, setSyncing] = useState(false);

  const load = useCallback(async () => {
    setLoadError(null);
    try {
      setSummary(await ecfService.getSummary());
    } catch (err) {
      setLoadError(err instanceof Error ? err.message : 'No se pudo cargar el panel');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  /**
   * Reintenta los envíos que quedaron sin salir: comprobantes sin enviar, veredictos sin
   * consultar y aprobaciones comerciales decididas que no llegaron al emisor. Al terminar se
   * recarga el resumen, porque los contadores cambian.
   */
  const handleSync = async () => {
    setSyncing(true);
    try {
      const r = await ecfService.processPending();
      const partes: string[] = [];
      if (r.emitidos.enviados) partes.push(`${r.emitidos.enviados} comprobante(s) enviado(s)`);
      if (r.emitidos.refrescados) partes.push(`${r.emitidos.refrescados} estado(s) actualizado(s)`);
      if (r.acecf.enviados) partes.push(`${r.acecf.enviados} aprobación(es) comunicada(s)`);

      const fallidos = r.emitidos.fallidos + r.acecf.fallidos;
      if (partes.length) toast.success(partes.join(' · '));
      else if (!fallidos) toast.info('No quedaba nada pendiente por procesar');

      if (fallidos) toast.error(`${fallidos} envío(s) siguen sin salir. Revisa el detalle.`);
      if (r.emitidos.sinXml) {
        toast.warning(
          `${r.emitidos.sinXml} comprobante(s) no tienen XML firmado guardado y hay que reemitirlos.`
        );
      }

      await load();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'No se pudieron procesar los pendientes');
    } finally {
      setSyncing(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </DashboardLayout>
    );
  }

  if (loadError || !summary) {
    return (
      <DashboardLayout>
        <div className="bg-red-50 text-red-700 border border-red-200 p-4 rounded-lg flex items-center justify-between gap-4">
          <span>
            <i className="ri-error-warning-line mr-2"></i>
            {loadError ?? 'No se pudo cargar el panel'}
          </span>
          <button onClick={load} className="text-sm font-medium hover:underline whitespace-nowrap">
            Reintentar
          </button>
        </div>
      </DashboardLayout>
    );
  }

  const { emitidos, recibidos, secuencias, certificado } = summary;
  const amb = AMBIENTE_BADGE[summary.ambiente] ?? AMBIENTE_BADGE.test;

  // Lo que se puede reintentar: comprobantes sin enviar más acuses que no salieron.
  const porProcesar = emitidos.pendiente + recibidos.acecfFallidos;

  // Alertas en orden de urgencia: primero lo que impide emitir, luego lo que se puede reintentar.
  const alertas: Alerta[] = [];

  if (certificado.status !== 'valid') {
    alertas.push({
      tone: 'bad',
      text:
        certificado.status === 'expired'
          ? 'El certificado digital está vencido. No se puede firmar ningún comprobante.'
          : 'No hay un certificado digital válido cargado. No se puede firmar ningún comprobante.',
      action: { label: 'Cargar certificado', to: '/taxes/ecf/configuracion?tab=emisor' },
    });
  } else if (certificado.diasParaVencer !== null && certificado.diasParaVencer <= 30) {
    alertas.push({
      tone: 'warn',
      text: `El certificado digital vence en ${certificado.diasParaVencer} días (${certificado.expiresAt}).`,
      action: { label: 'Ver certificado', to: '/taxes/ecf/configuracion?tab=emisor' },
    });
  }

  if (emitidos.rechazado > 0) {
    alertas.push({
      tone: 'bad',
      text: `${emitidos.rechazado} comprobante(s) rechazado(s) por la DGII. Hay que corregirlos y volver a emitir.`,
      action: { label: 'Ver rechazados', to: '/taxes/ecf/emitidos?estado=rechazado' },
    });
  }

  if (emitidos.pendiente > 0) {
    alertas.push({
      tone: 'warn',
      text: `${emitidos.pendiente} comprobante(s) sin enviar a la DGII.`,
      action: { label: 'Ver pendientes', to: '/taxes/ecf/emitidos?estado=pendiente' },
    });
  }

  if (emitidos.rechazadosPorCliente > 0) {
    alertas.push({
      tone: 'warn',
      text: `${emitidos.rechazadosPorCliente} comprobante(s) rechazado(s) comercialmente por el cliente.`,
      action: { label: 'Ver emitidos', to: '/taxes/ecf/emitidos' },
    });
  }

  if (recibidos.pendientes > 0) {
    alertas.push({
      tone: 'warn',
      text: `${recibidos.pendientes} comprobante(s) de proveedores esperan tu aprobación comercial.`,
      action: { label: 'Revisar recibidos', to: '/taxes/ecf/recibidos?estado=pendiente' },
    });
  }

  if (recibidos.acecfFallidos > 0) {
    alertas.push({
      tone: 'bad',
      text: `${recibidos.acecfFallidos} aprobación(es) comercial(es) decidida(s) pero no comunicada(s) al emisor.`,
      action: { label: 'Reintentar envío', to: '/taxes/ecf/recibidos' },
    });
  }

  secuencias.vencidas.forEach((s) => {
    alertas.push({
      tone: 'bad',
      text: `El rango del tipo ${s.doc_type} (${DOC_TYPE_LABEL[s.doc_type]}) venció el ${s.expires_at}.`,
      action: { label: 'Ver secuencias', to: '/taxes/ecf/configuracion?tab=secuencias' },
    });
  });

  secuencias.sinRango.forEach((t) => {
    alertas.push({
      tone: 'bad',
      text: `No hay ningún rango disponible para el tipo ${t} (${DOC_TYPE_LABEL[t]}), y ya se han emitido comprobantes de ese tipo.`,
      action: { label: 'Agregar rango', to: '/taxes/ecf/configuracion?tab=secuencias' },
    });
  });

  secuencias.bajas.forEach((s) => {
    alertas.push({
      tone: 'warn',
      text: `Quedan ${s.remaining} secuenciales del tipo ${s.doc_type} (${DOC_TYPE_LABEL[s.doc_type]}).`,
      action: { label: 'Ver secuencias', to: '/taxes/ecf/configuracion?tab=secuencias' },
    });
  });

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Encabezado */}
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Panel e-CF</h1>
            <p className="text-gray-600">
              Estado de la facturación electrónica y lo que requiere atención
            </p>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            <EcfBadge variant={amb.variant}>Ambiente: {amb.label}</EcfBadge>
            <button
              onClick={handleSync}
              disabled={syncing || porProcesar === 0}
              title={
                porProcesar === 0
                  ? 'No hay nada pendiente por procesar'
                  : 'Reintenta los envíos que quedaron sin salir'
              }
              className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors whitespace-nowrap disabled:bg-gray-400 disabled:cursor-not-allowed"
            >
              <i className={`${syncing ? 'ri-loader-4-line animate-spin' : 'ri-play-circle-line'} mr-2`}></i>
              {syncing ? 'Procesando...' : `Procesar pendientes (${porProcesar})`}
            </button>
            <button
              onClick={load}
              disabled={syncing}
              className="bg-white border border-gray-300 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-50 transition-colors whitespace-nowrap disabled:opacity-50"
            >
              <i className="ri-refresh-line mr-2"></i>
              Actualizar
            </button>
            <button
              onClick={() => navigate('/taxes')}
              className="bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700 transition-colors whitespace-nowrap"
            >
              <i className="ri-arrow-left-line mr-2"></i>
              Volver
            </button>
          </div>
        </div>

        {/* Emitido */}
        <div>
          <h2 className="text-sm font-semibold text-gray-900 mb-3">Emitido</h2>
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-6">
            <StatCard
              icon="ri-send-plane-line"
              label="Emitidos hoy"
              value={emitidos.hoy}
              hint={`${emitidos.total} en total`}
            />
            <StatCard
              icon="ri-checkbox-circle-line"
              label="Aceptados"
              value={emitidos.aceptado}
              tone="good"
              to="/taxes/ecf/emitidos?estado=aceptado"
            />
            <StatCard
              icon="ri-time-line"
              label="En proceso"
              value={emitidos.enproceso}
              tone={emitidos.enproceso > 0 ? 'warn' : 'neutral'}
              to="/taxes/ecf/emitidos?estado=enproceso"
            />
            <StatCard
              icon="ri-error-warning-line"
              label="Sin enviar"
              value={emitidos.pendiente}
              tone={emitidos.pendiente > 0 ? 'warn' : 'neutral'}
              to="/taxes/ecf/emitidos?estado=pendiente"
            />
            <StatCard
              icon="ri-close-circle-line"
              label="Rechazados"
              value={emitidos.rechazado}
              tone={emitidos.rechazado > 0 ? 'bad' : 'neutral'}
              to="/taxes/ecf/emitidos?estado=rechazado"
            />
          </div>
        </div>

        {/* Recibido y configuración */}
        <div>
          <h2 className="text-sm font-semibold text-gray-900 mb-3">Recibido y configuración</h2>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
            <StatCard
              icon="ri-inbox-line"
              label="Recibidos de proveedores"
              value={recibidos.total}
              to="/taxes/ecf/recibidos"
            />
            <StatCard
              icon="ri-question-answer-line"
              label="Esperan aprobación"
              value={recibidos.pendientes}
              tone={recibidos.pendientes > 0 ? 'warn' : 'neutral'}
              to="/taxes/ecf/recibidos?estado=pendiente"
            />
            <StatCard
              icon="ri-list-ordered"
              label="Rangos con poco saldo"
              value={secuencias.bajas.length + secuencias.vencidas.length}
              tone={secuencias.bajas.length + secuencias.vencidas.length > 0 ? 'warn' : 'neutral'}
              to="/taxes/ecf/configuracion?tab=secuencias"
            />

            {/* Tarjeta del certificado: no es un contador, así que se arma aparte */}
            <Link to="/taxes/ecf/configuracion?tab=emisor" className="block h-full">
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 h-full transition-colors hover:bg-gray-50">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-sm text-gray-600">Certificado digital</p>
                    <p
                      className={`text-2xl font-bold mt-1 ${
                        certificado.status === 'valid' ? 'text-green-600' : 'text-red-600'
                      }`}
                    >
                      {certificado.status === 'valid'
                        ? 'Válido'
                        : certificado.status === 'expired'
                          ? 'Vencido'
                          : 'Sin cargar'}
                    </p>
                    {certificado.expiresAt && (
                      <p className="text-xs text-gray-500 mt-1">
                        Vence {certificado.expiresAt}
                        {certificado.diasParaVencer !== null &&
                          ` · ${certificado.diasParaVencer} días`}
                      </p>
                    )}
                  </div>
                  <i
                    className={`ri-key-2-line text-xl shrink-0 ${
                      certificado.status === 'valid' ? 'text-green-600' : 'text-red-600'
                    }`}
                  ></i>
                </div>
              </div>
            </Link>
          </div>
        </div>

        {/* Requiere atención */}
        <div>
          <h2 className="text-sm font-semibold text-gray-900 mb-3">Requiere atención</h2>

          {alertas.length === 0 ? (
            <div className="bg-green-50 text-green-700 border border-green-200 p-4 rounded-lg flex items-center gap-2">
              <i className="ri-checkbox-circle-line"></i>
              Todo al día. No hay comprobantes pendientes ni rangos por agotarse.
            </div>
          ) : (
            <div className="space-y-2">
              {alertas.map((a, i) => (
                <div
                  key={i}
                  className={`flex items-center justify-between gap-4 p-4 rounded-lg border ${
                    a.tone === 'bad'
                      ? 'bg-red-50 border-red-200 text-red-700'
                      : 'bg-amber-50 border-amber-200 text-amber-800'
                  }`}
                >
                  <div className="flex items-start gap-2 min-w-0">
                    <i
                      className={`${
                        a.tone === 'bad' ? 'ri-close-circle-line' : 'ri-error-warning-line'
                      } mt-0.5 shrink-0`}
                    ></i>
                    <span className="text-sm">{a.text}</span>
                  </div>
                  {a.action && (
                    <Link
                      to={a.action.to}
                      className="text-sm font-medium whitespace-nowrap hover:underline shrink-0"
                    >
                      {a.action.label}
                      <i className="ri-arrow-right-line ml-1"></i>
                    </Link>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
}
